import {takeRight, upperFirst} from 'lodash';
import moment from 'moment';
import React, {useEffect, useMemo, useRef} from 'react';
import {Field, useForm} from 'react-final-form';
import {Loader, Form as SemanticForm} from 'semantic-ui-react';
import {cx} from '@emotion/css';
import {css} from '@emotion/react/macro';
import {useMatch} from '@reach/router';
import {analytics} from '../../analytics';
import {CardBrands} from '../../api/generated/enums';
import {CardBrandImage} from '../../components/card-brand';
import {Form} from '../../forms';
import {SubmitError} from '../../forms/submit-error';
import {useProduce} from '../../hooks/use-produce';
import {routes} from '../../routes';
import {useScript} from '../../utils';
import {logger} from '../../utils/logger';
import {BillingAddressFormFields} from '../billing-address-form-fields';
import {creditCardKeys, fieldKeys} from '../index';
import {useTokenizationConfig} from '../tokenization-context';
import {CardTokenizerProps} from '../types';
import {CollectJsConfig, TokenizationReponse} from './types';

const log = logger('nmi tokenizer');

interface State {
  initialized: boolean;
  tokenizing: boolean;
  fieldState: {
    ccnumber: {
      valid: boolean;
      message: string;
      touched: boolean;
    };
    ccexp: {valid: boolean; message: string; touched: boolean};
    cvv: {valid: boolean; message: string; touched: boolean};
  };
  tokenizationReponse?: TokenizationReponse;
}

const initializeCollectJs = (config: CollectJsConfig) => {
  (window as any).CollectJS.configure({
    customCss: {},
    // more css options available
    validationCallback: config.validationCallback,
    fieldsAvailableCallback: config.fieldsAvailableCallback,
    fields: {
      ccnumber: {
        selector: `#${creditCardKeys.accountNumberToken}`,
        title: 'Card Number',
        placeholder: '0000 0000 0000 0000',
      },
      ccexp: {
        selector: `#${creditCardKeys.cardExpiration}`,
        title: 'Expiration',
        placeholder: '00 / 00',
      },
      cvv: {
        display: 'show',
        selector: `#${creditCardKeys.cardCvv}`,
        title: 'CVV Code',
        placeholder: '***',
      },
    },
    variant: 'inline',
    callback: config.tokenizeCallback,
  });
};

// FIXME: Move to backend
const cardBrandMap = {
  amex: CardBrands.Amex,
  discover: CardBrands.Discover,
  mastercard: CardBrands.Mastercard,
  visa: CardBrands.Visa,
};

export const buildCollectJsToken = (response: TokenizationReponse) => {
  if (response.token && response.card) {
    const {
      token,
      card: {number, exp, type, hash},
    } = response;

    // prefix:token|last4|expiration|type|hash
    const last4 = takeRight(number, 4).join('');
    const tokenParts = [token, last4, exp, type, hash].join('|');
    const nmiToken = `nmi_card:${tokenParts}`;
    const accountNumberMasked = `************${last4}`;

    return {
      token: nmiToken,
      type,
      exp,
      accountNumberMasked,
    };
  }

  return undefined;
};

export const NmiFields: React.FC<CardTokenizerProps> = ({
  onLoad,
  onCardInfo,
  billingAddressCollectionType,
  cvv = true,
}) => {
  const targetInputRef = useRef<HTMLInputElement>(null);
  const config = useTokenizationConfig<{tokenizationKey: string}>();
  const [state, setState] = useProduce<State>({
    initialized: false,
    tokenizing: false,
    fieldState: {
      ccnumber: {valid: false, message: '', touched: false},
      ccexp: {valid: false, message: '', touched: false},
      cvv: {valid: false, message: '', touched: false},
    },
  });
  const isCheckoutPage = useMatch(routes.checkout.base);

  const {initialized} = state;
  const tokenizationKey = config?.properties.tokenizationKey || '';

  const form = useForm();

  const collectJsConfig = useMemo(() => {
    const scriptConfig = {
      url: 'https://paystar.transactiongateway.com/token/Collect.js',
      attributes: {
        'data-tokenization-key': tokenizationKey,
      },
      onload: () => {
        if (!initialized) {
          setState((draft) => {
            draft.initialized = true;
          });

          analytics.trackEvent('nmi.tokenizer-loaded', {
            subject: tokenizationKey,
          });

          initializeCollectJs({
            fieldsAvailableCallback: () => {
              onLoad?.();
            },
            validationCallback: (field, valid, message) => {
              setState((draft) => {
                const fieldState = draft.fieldState[field];
                fieldState.valid = valid;
                fieldState.message = message;
                fieldState.touched = true;
              });
            },
            tokenizeCallback: (response) => {
              setState((draft) => {
                draft.tokenizationReponse = response;
                draft.tokenizing = false;
              });

              const tokenInfo = buildCollectJsToken(response);

              if (tokenInfo) {
                form.batch(() => {
                  form.change(fieldKeys.accountNumberToken, tokenInfo.token);
                  form.change(fieldKeys.accountNumberMasked, tokenInfo.accountNumberMasked);
                  const parsedExpiration = moment(tokenInfo.exp ?? '', 'MMYY');
                  const expiration = parsedExpiration.format('MM/YY');

                  form.change(fieldKeys.cardExpiration, expiration);
                });

                const brand = cardBrandMap[tokenInfo.type] || CardBrands.Unknown;

                onCardInfo?.({type: brand});
              }
            },
          });
        }
      },
    };

    log.info('initializing Collect.js', scriptConfig);

    return scriptConfig;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, tokenizationKey]);

  useScript(collectJsConfig);

  const canTokenize = useMemo(() => {
    const {ccnumber, ccexp, cvv} = state.fieldState;
    return ccnumber.valid && ccexp.valid && cvv.valid;
  }, [state.fieldState]);

  useEffect(() => {
    if (canTokenize) {
      setState((draft) => {
        draft.tokenizing = true;
      });
      (window as any).CollectJS.startPaymentRequest();
    }
  }, [canTokenize, setState]);

  var cardBrand = upperFirst(state.tokenizationReponse?.card?.type || CardBrands.Unknown);

  return (
    <>
      <Field
        id={fieldKeys.accountHolderName}
        name={fieldKeys.accountHolderName}
        render={({input, meta}) => {
          return (
            <div className="form-field">
              <SemanticForm.Field error={!!meta.submitError}>
                <label htmlFor={input.name}>Name on Card</label>
                <div className="ui input">
                  <input
                    {...input}
                    id={input.name}
                    ref={targetInputRef}
                    autoFocus={!isCheckoutPage}
                  />
                </div>
              </SemanticForm.Field>
              <SubmitError name={input.name} />
            </div>
          );
        }}
      />

      <div css={nmiStyles}>
        <Field
          id={`${fieldKeys.accountNumberToken}_field`}
          name={fieldKeys.accountNumberToken}
          render={({input, meta}) => {
            return (
              <div className="form-field">
                <SemanticForm.Field error={!!meta.submitError}>
                  <label htmlFor={input.name}>Card Number</label>
                  <div className="input-with-image">
                    <div
                      className={cx('ui input nmi-tokenized-field', {
                        error: !!meta.submitError,
                      })}
                      style={{width: '100%'}}
                      id={fieldKeys.accountNumberToken}
                    ></div>
                    {state.tokenizing ? (
                      <Loader active inline className="tokenization-loader" size="small" />
                    ) : (
                      <CardBrandImage type={cardBrand} className="card-preview" />
                    )}
                  </div>
                </SemanticForm.Field>
                <SubmitError name={input.name} />
              </div>
            );
          }}
        />

        <Form.Row>
          <Field
            id={`${fieldKeys.cardExpiration}_field`}
            name={fieldKeys.cardExpiration}
            render={({input, meta}) => {
              return (
                <div className="form-field">
                  <SemanticForm.Field error={!!meta.submitError}>
                    <label htmlFor={input.name}>Expiration</label>
                    <div
                      className={cx('ui input input-with-image nmi-tokenized-field', {
                        error: !!meta.submitError,
                      })}
                      style={{width: '100%'}}
                      id={fieldKeys.cardExpiration}
                    >
                      {/* <CardBrandImage
                        type={cardInfo.type}
                        className="card-preview"
                      /> */}
                    </div>
                  </SemanticForm.Field>
                  <SubmitError name={input.name} />
                </div>
              );
            }}
          />

          <Field
            id={`${fieldKeys.cardCvv}_field`}
            name={fieldKeys.cardCvv}
            render={({input, meta}) => {
              return (
                <div className="form-field">
                  <SemanticForm.Field error={!!meta.submitError}>
                    <label htmlFor={input.name}>CVV</label>
                    <div
                      className={cx('ui input input-with-image nmi-tokenized-field', {
                        error: !!meta.submitError,
                      })}
                      style={{width: '100%'}}
                      id={fieldKeys.cardCvv}
                    >
                      {/* <CardBrandImage
                        type={cardInfo.type}
                        className="card-preview"
                      /> */}
                    </div>
                  </SemanticForm.Field>
                  <SubmitError name={input.name} />
                </div>
              );
            }}
          />
        </Form.Row>
      </div>

      <BillingAddressFormFields
        billingAddressCollectionType={billingAddressCollectionType}
        paymentSourceType="Credit Card"
      />
    </>
  );
};

const nmiStyles = css`
  .nmi-tokenized-field {
    font-family: 'Roboto', 'Helvetica Neue', Arial, Helvetica, sans-serif;
    margin: 0em;
    outline: none;
    width: 100%;
    line-height: 1.21428571em;
    font-size: 1em;
    background: #ffffff;
    color: #0a2233;
    border-radius: 0.26666667rem;
    box-shadow: 0em 0em 0em 0em transparent inset;
    min-height: 40px;
    background: #f3f3f3;
  }

  .input-with-image {
    position: relative;

    .ui.image {
      position: absolute;
      max-height: 2rem;
      right: 0.4rem;
      top: 0.4rem;
    }

    .tokenization-loader {
      position: absolute !important;
      top: 0.6rem !important;
      right: 0.5rem !important;
      left: unset !important;
    }
  }
`;
